import React, { useMemo, useState } from "react";

const DEFAULT_VALUES = {
  totalAgentCost: 1000,
  successfulOutcomes: 80,
  totalAttempts: 100,
  modelCost: 420,
  toolApiCost: 120,
  infrastructureCost: 80,
  humanCost: 160,
  governanceCost: 60,
  failureRecoveryCost: 40,
  baselineCost: 1800,
  valueCreated: 4200,
  economicBenefit: 4200,
  valueProduced: 4200,
  initialInvestment: 12000,
  monthlyNetBenefit: 2400,
  fixedCost: 5000,
  savingsPerOutcome: 10,
  changeInCost: 100,
  changeInOutcomes: 20,
  completedOutcomes: 80,
  timeHours: 8,
  baselineThroughput: 6,
  baselineTime: 0.75,
  agentTime: 0.25,
  numberOfOutcomes: 80,
  tasksCompletedAutonomously: 72,
  totalTasks: 100,
  tasksRequiringHumanIntervention: 28,
  totalHumanMinutes: 240,
  humanHours: 4,
  outcomesMeetingQualityThreshold: 76,
  outputsAcceptedWithoutCorrection: 70,
  outputsProduced: 80,
  failedTasks: 8,
  retries: 12,
  probabilityOfFailure: 0.08,
  impactOfFailure: 250,
  operatingCost: 900,
  tokensConsumed: 2400000,
  modelSpend: 420,
  toolCalls: 320,
  agentSteps: 640,
  startTime: 0,
  finishTime: 0.25,
  revenue: 10000,
  cogs: 3800,
  aiCogs: 900,
  customerRevenue: 500,
  variableAiCost: 45,
  truePositives: 76,
  falsePositives: 4,
  falseNegatives: 8,
};

const INPUT_SECTIONS = [
  {
    title: "Cost and value inputs",
    description: "Use one consistent currency throughout this section.",
    fields: [
      ["totalAgentCost", "Total agent cost"],
      ["modelCost", "Model cost"],
      ["toolApiCost", "Tool / API cost"],
      ["infrastructureCost", "Infrastructure cost"],
      ["humanCost", "Human cost"],
      ["governanceCost", "Governance cost"],
      ["failureRecoveryCost", "Failure / recovery cost"],
      ["baselineCost", "Baseline cost"],
      ["valueCreated", "Value created"],
      ["economicBenefit", "Economic benefit"],
      ["valueProduced", "Value produced"],
      ["initialInvestment", "Initial investment"],
      ["monthlyNetBenefit", "Monthly net benefit"],
      ["fixedCost", "Fixed cost"],
      ["savingsPerOutcome", "Savings per outcome"],
      ["changeInCost", "Change in cost"],
      ["changeInOutcomes", "Change in outcomes"],
      ["operatingCost", "Operating cost"],
      ["impactOfFailure", "Impact of failure"],
      ["revenue", "Revenue"],
      ["cogs", "COGS"],
      ["aiCogs", "AI COGS"],
      ["customerRevenue", "Customer revenue"],
      ["variableAiCost", "Variable AI cost"],
    ],
  },
  {
    title: "Outcomes, time, and human work",
    description: "Enter counts for the same measurement period.",
    fields: [
      ["successfulOutcomes", "Successful outcomes"],
      ["totalAttempts", "Total attempts"],
      ["completedOutcomes", "Completed outcomes"],
      ["timeHours", "Elapsed time (hours)"],
      ["baselineThroughput", "Baseline throughput"],
      ["baselineTime", "Baseline time per outcome (hours)"],
      ["agentTime", "Agent time per outcome (hours)"],
      ["numberOfOutcomes", "Number of outcomes"],
      ["tasksCompletedAutonomously", "Tasks completed autonomously"],
      ["totalTasks", "Total tasks"],
      ["tasksRequiringHumanIntervention", "Tasks requiring human intervention"],
      ["totalHumanMinutes", "Total human minutes"],
      ["humanHours", "Human hours"],
      ["outcomesMeetingQualityThreshold", "Outcomes meeting quality threshold"],
      ["outputsAcceptedWithoutCorrection", "Outputs accepted without correction"],
      ["outputsProduced", "Outputs produced"],
      ["failedTasks", "Failed tasks"],
      ["retries", "Retries"],
    ],
  },
  {
    title: "Model, risk, and quality inputs",
    description: "Use probability as a decimal: 8% = 0.08.",
    fields: [
      ["probabilityOfFailure", "Probability of failure"],
      ["tokensConsumed", "Tokens consumed"],
      ["modelSpend", "Model spend"],
      ["toolCalls", "Tool calls"],
      ["agentSteps", "Agent steps"],
      ["startTime", "Start time (hours)"],
      ["finishTime", "Finish time (hours)"],
      ["truePositives", "True positives"],
      ["falsePositives", "False positives"],
      ["falseNegatives", "False negatives"],
    ],
  },
];

const PRIORITY_METRIC_KEYS = [
  "fullyLoaded",
  "costPerOutcome",
  "successRate",
  "costSavings",
  "savingsPercent",
  "roi",
  "valuePerDollar",
  "qualityAdjustedCost",
  "automationRate",
  "failureRate",
];

const MATH_FORMULAS = {
  efficiency: "V ÷ C₍FL₎",
  costPerOutcome: "C₍A₎ ÷ O₍S₎",
  successRate: "O₍S₎ ÷ A₍T₎",
  successAdjustedCost: "(C₍A₎ ÷ A₍T₎) ÷ (O₍S₎ ÷ A₍T₎)",
  costPerAttempt: "C₍A₎ ÷ A₍T₎",
  fullyLoaded: "C₍M₎ + C₍T₎ + C₍I₎ + C₍H₎ + C₍G₎ + C₍R₎",
  costSavings: "C₍B₎ − C₍A₎",
  savingsPercent: "(C₍B₎ − C₍A₎) ÷ C₍B₎ × 100",
  roi: "(V − C₍A₎) ÷ C₍A₎",
  benefitCost: "B ÷ C₍A₎",
  valuePerDollar: "V ÷ C₍A₎",
  costToValue: "C₍A₎ ÷ V",
  payback: "I₀ ÷ B₍M₎",
  breakEven: "F ÷ S₍O₎",
  marginalCost: "ΔC ÷ ΔO",
  throughput: "O₍C₎ ÷ T",
  productivityGain: "Q₍N₎ ÷ Q₍B₎",
  timeSaved: "T₍B₎ − T₍A₎",
  hoursSaved: "N₍O₎ × (T₍B₎ − T₍A₎)",
  automationRate: "N₍AUTO₎ ÷ N₍TASKS₎",
  escalationRate: "N₍HUMAN₎ ÷ N₍TASKS₎",
  humanMinutes: "M₍HUMAN₎ ÷ O₍C₎",
  humanLeverage: "O₍C₎ ÷ H₍HUMAN₎",
  qualityAdjustedCost: "C₍FL₎ ÷ O₍Q₎",
  acceptanceRate: "O₍ACCEPT₎ ÷ O₍PRODUCED₎",
  failureRate: "F₍TASKS₎ ÷ A₍T₎",
  retryRate: "R ÷ N₍TASKS₎",
  expectedFailureCost: "p₍F₎ × I₍F₎",
  riskAdjustedCost: "C₍OP₎ + E[C₍F₎]",
  tokensPerOutcome: "N₍TOKENS₎ ÷ O₍S₎",
  modelCostPerOutcome: "C₍M₎ ÷ O₍S₎",
  toolCallsPerOutcome: "N₍TOOLS₎ ÷ O₍S₎",
  agentStepsPerOutcome: "N₍STEPS₎ ÷ O₍S₎",
  retriesPerOutcome: "R ÷ O₍S₎",
  timeToOutcome: "t₍FINISH₎ − t₍START₎",
  grossMargin: "(R − COGS) ÷ R",
  aiCostRevenue: "C₍AI₎ ÷ R × 100",
  contributionMargin: "R₍CUSTOMER₎ − C₍AI,VAR₎",
  precision: "TP ÷ (TP + FP)",
  recall: "TP ÷ (TP + FN)",
};

const EMPTY_VALUES = Object.fromEntries(
  Object.keys(DEFAULT_VALUES).map((key) => [key, ""])
);

function summarizeLiveTelemetry(rows = []) {
  const totals = rows.reduce(
    (result, row) => {
      for (const key of [
        "interactions",
        "successfulInteractions",
        "failedInteractions",
        "modelCalls",
        "inputTokens",
        "outputTokens",
        "totalLatencyMs",
      ]) {
        result[key] += Number(row?.[key]) || 0;
      }
      result.estimatedCostUsd += Number(row?.estimatedCostUsd) || 0;
      result.knownCostInteractions += Number(row?.knownCostInteractions) || 0;
      return result;
    },
    {
      interactions: 0,
      successfulInteractions: 0,
      failedInteractions: 0,
      modelCalls: 0,
      inputTokens: 0,
      outputTokens: 0,
      totalLatencyMs: 0,
      estimatedCostUsd: 0,
      knownCostInteractions: 0,
    }
  );

  const averageLatencyHours = totals.interactions
    ? totals.totalLatencyMs / totals.interactions / 3_600_000
    : "";
  const failureProbability = totals.interactions
    ? totals.failedInteractions / totals.interactions
    : "";
  const measuredCost = totals.knownCostInteractions > 0 ? totals.estimatedCostUsd : "";

  return {
    totalAgentCost: measuredCost,
    modelCost: measuredCost,
    modelSpend: measuredCost,
    successfulOutcomes: totals.successfulInteractions || "",
    totalAttempts: totals.interactions || "",
    completedOutcomes: totals.successfulInteractions || "",
    timeHours: totals.totalLatencyMs ? totals.totalLatencyMs / 3_600_000 : "",
    numberOfOutcomes: totals.successfulInteractions || "",
    totalTasks: totals.interactions || "",
    failedTasks: totals.failedInteractions || "",
    probabilityOfFailure: failureProbability,
    tokensConsumed: totals.inputTokens + totals.outputTokens || "",
    outputsProduced: totals.interactions || "",
    startTime: totals.interactions ? 0 : "",
    finishTime: averageLatencyHours,
  };
}

function safeDivide(numerator, denominator) {
  const top = Number(numerator) || 0;
  const bottom = Number(denominator) || 0;
  return bottom > 0 ? top / bottom : null;
}

function formatNumber(value, suffix = "") {
  if (value === null || value === undefined || !Number.isFinite(value)) return "—";
  return `${new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(value)}${suffix}`;
}

function formatCurrency(value, currency = "USD") {
  return value === null || value === undefined || !Number.isFinite(value)
    ? "—"
    : new Intl.NumberFormat(undefined, {
        style: "currency",
        currency,
        maximumFractionDigits: 2,
      }).format(value);
}

function formatPercent(value) {
  return value === null || value === undefined || !Number.isFinite(value)
    ? "—"
    : formatNumber(value * 100, "%");
}

function MetricCard({ label, formula, mathFormula, value, kind = "number", featured = false, currency = "USD" }) {
  const renderedValue =
    kind === "percent"
      ? formatPercent(value)
      : kind === "currency"
        ? formatCurrency(value, currency)
        : kind === "ratio"
          ? formatNumber(value, "×")
          : kind === "hours"
            ? formatNumber(value, " h")
            : formatNumber(value);

  return (
    <article className={`ai-economics-metric ${featured ? "is-featured" : ""}`}>
      <h4 className="ai-economics-metric-label">{label}</h4>
      <strong>{renderedValue}</strong>
      <div className="ai-economics-metric-formula">
        <span>Formula</span>
        <strong className="ai-economics-math-formula">{mathFormula || formula}</strong>
        <code>{formula}</code>
      </div>
    </article>
  );
}

export function AiEconomicsCalculatorPage() {
  const [values, setValues] = useState(EMPTY_VALUES);
  const [currency, setCurrency] = useState("USD");

  const metrics = useMemo(() => {
    const hasValues = (...keys) => keys.every((key) => values[key] !== "" && Number.isFinite(Number(values[key])));
    const fullyLoadedKeys = ["modelCost", "toolApiCost", "infrastructureCost", "humanCost", "governanceCost", "failureRecoveryCost"];
    const costPerAttempt = safeDivide(values.totalAgentCost, values.totalAttempts);
    const fullyLoadedAgentCost = hasValues(...fullyLoadedKeys)
      ? fullyLoadedKeys.reduce((total, key) => total + Number(values[key]), 0)
      : null;
    const expectedFailureCost = hasValues("probabilityOfFailure", "impactOfFailure")
      ? Number(values.probabilityOfFailure) * Number(values.impactOfFailure)
      : null;
    const timeToOutcome = hasValues("finishTime", "startTime")
      ? Number(values.finishTime) - Number(values.startTime)
      : null;

    return {
      efficiency: ["Agent Efficiency", "Value Created / Fully Loaded Agent Cost", safeDivide(values.valueCreated, fullyLoadedAgentCost), "ratio"],
      best: ["Cost per Successful, Quality-Compliant Outcome", "Total Fully Loaded Cost / Successful Outcomes Meeting Required Quality Threshold", safeDivide(fullyLoadedAgentCost, values.outcomesMeetingQualityThreshold), "currency"],
      costPerOutcome: ["Cost per Outcome", "Total Agent Cost / Successful Outcomes", safeDivide(values.totalAgentCost, values.successfulOutcomes), "currency"],
      successRate: ["Success Rate", "Successful Outcomes / Total Attempts", safeDivide(values.successfulOutcomes, values.totalAttempts), "percent"],
      successAdjustedCost: ["Success-Adjusted Cost", "Cost per Attempt / Success Rate", safeDivide(costPerAttempt, safeDivide(values.successfulOutcomes, values.totalAttempts)), "currency"],
      costPerAttempt: ["Cost per Attempt", "Total Agent Cost / Total Attempts", costPerAttempt, "currency"],
      fullyLoaded: ["Fully Loaded Agent Cost", "Model + Tool/API + Infrastructure + Human + Governance + Failure/Recovery", fullyLoadedAgentCost, "currency"],
      costSavings: ["Cost Savings", "Baseline Cost - Agent Cost", hasValues("baselineCost", "totalAgentCost") ? Number(values.baselineCost) - Number(values.totalAgentCost) : null, "currency"],
      savingsPercent: ["Savings %", "(Baseline Cost - Agent Cost) / Baseline Cost × 100", hasValues("baselineCost", "totalAgentCost") ? safeDivide(Number(values.baselineCost) - Number(values.totalAgentCost), values.baselineCost) : null, "percent"],
      roi: ["ROI", "(Value Created - Agent Cost) / Agent Cost", hasValues("valueCreated", "totalAgentCost") ? safeDivide(Number(values.valueCreated) - Number(values.totalAgentCost), values.totalAgentCost) : null, "percent"],
      benefitCost: ["Benefit-Cost Ratio", "Economic Benefit / Agent Cost", safeDivide(values.economicBenefit, values.totalAgentCost), "ratio"],
      valuePerDollar: ["Value per Dollar", "Value Produced / Agent Cost", safeDivide(values.valueProduced, values.totalAgentCost), "ratio"],
      costToValue: ["Cost-to-Value Ratio", "Agent Cost / Value Produced", safeDivide(values.totalAgentCost, values.valueProduced), "ratio"],
      payback: ["Payback Period", "Initial Investment / Monthly Net Benefit", safeDivide(values.initialInvestment, values.monthlyNetBenefit), "months"],
      breakEven: ["Break-Even Volume", "Fixed Cost / Savings per Outcome", safeDivide(values.fixedCost, values.savingsPerOutcome), "number"],
      marginalCost: ["Marginal Cost", "Change in Cost / Change in Outcomes", safeDivide(values.changeInCost, values.changeInOutcomes), "currency"],
      throughput: ["Throughput", "Completed Outcomes / Time", safeDivide(values.completedOutcomes, values.timeHours), "number"],
      productivityGain: ["Productivity Gain", "New Throughput / Baseline Throughput", safeDivide(safeDivide(values.completedOutcomes, values.timeHours), values.baselineThroughput), "ratio"],
      timeSaved: ["Time Saved per Outcome", "Baseline Time - Agent Time", hasValues("baselineTime", "agentTime") ? Number(values.baselineTime) - Number(values.agentTime) : null, "hours"],
      hoursSaved: ["Total Hours Saved", "Number of Outcomes × Time Saved per Outcome", hasValues("numberOfOutcomes", "baselineTime", "agentTime") ? Number(values.numberOfOutcomes) * (Number(values.baselineTime) - Number(values.agentTime)) : null, "hours"],
      automationRate: ["Automation Rate", "Tasks Completed Autonomously / Total Tasks", safeDivide(values.tasksCompletedAutonomously, values.totalTasks), "percent"],
      escalationRate: ["Human Escalation Rate", "Tasks Requiring Human Intervention / Total Tasks", safeDivide(values.tasksRequiringHumanIntervention, values.totalTasks), "percent"],
      humanMinutes: ["Human Minutes per Outcome", "Total Human Minutes / Completed Outcomes", safeDivide(values.totalHumanMinutes, values.completedOutcomes), "number"],
      humanLeverage: ["Human Leverage", "Outcomes Produced / Human Hours", safeDivide(values.completedOutcomes, values.humanHours), "number"],
      qualityAdjustedCost: ["Quality-Adjusted Cost", "Total Cost / Outcomes Meeting Quality Threshold", safeDivide(fullyLoadedAgentCost, values.outcomesMeetingQualityThreshold), "currency"],
      acceptanceRate: ["Acceptance Rate", "Outputs Accepted Without Correction / Outputs Produced", safeDivide(values.outputsAcceptedWithoutCorrection, values.outputsProduced), "percent"],
      failureRate: ["Failure Rate", "Failed Tasks / Total Attempts", safeDivide(values.failedTasks, values.totalAttempts), "percent"],
      retryRate: ["Retry Rate", "Retries / Total Tasks", safeDivide(values.retries, values.totalTasks), "percent"],
      expectedFailureCost: ["Expected Failure Cost", "Probability of Failure × Impact of Failure", expectedFailureCost, "currency"],
      riskAdjustedCost: ["Risk-Adjusted Cost", "Operating Cost + Expected Failure Cost", hasValues("operatingCost") && expectedFailureCost !== null ? Number(values.operatingCost) + expectedFailureCost : null, "currency"],
      tokensPerOutcome: ["Tokens per Outcome", "Tokens Consumed / Successful Outcomes", safeDivide(values.tokensConsumed, values.successfulOutcomes), "number"],
      modelCostPerOutcome: ["Model Cost per Outcome", "Model Spend / Successful Outcomes", safeDivide(values.modelSpend, values.successfulOutcomes), "currency"],
      toolCallsPerOutcome: ["Tool Calls per Outcome", "Tool Calls / Successful Outcomes", safeDivide(values.toolCalls, values.successfulOutcomes), "number"],
      agentStepsPerOutcome: ["Agent Steps per Outcome", "Agent Steps / Successful Outcomes", safeDivide(values.agentSteps, values.successfulOutcomes), "number"],
      retriesPerOutcome: ["Retries per Outcome", "Retries / Successful Outcomes", safeDivide(values.retries, values.successfulOutcomes), "number"],
      timeToOutcome: ["Time to Outcome", "Finish Time - Start Time", timeToOutcome >= 0 ? timeToOutcome : null, "hours"],
      grossMargin: ["Gross Margin", "(Revenue - COGS) / Revenue", hasValues("revenue", "cogs") ? safeDivide(Number(values.revenue) - Number(values.cogs), values.revenue) : null, "percent"],
      aiCostRevenue: ["AI Cost as % of Revenue", "AI COGS / Revenue × 100", hasValues("aiCogs", "revenue") ? safeDivide(values.aiCogs, values.revenue) : null, "percent"],
      contributionMargin: ["Contribution Margin per Customer", "Customer Revenue - Variable AI Cost", hasValues("customerRevenue", "variableAiCost") ? Number(values.customerRevenue) - Number(values.variableAiCost) : null, "currency"],
      precision: ["Precision", "True Positives / (True Positives + False Positives)", safeDivide(values.truePositives, Number(values.truePositives || 0) + Number(values.falsePositives || 0)), "percent"],
      recall: ["Recall", "True Positives / (True Positives + False Negatives)", safeDivide(values.truePositives, Number(values.truePositives || 0) + Number(values.falseNegatives || 0)), "percent"],
    };
  }, [values]);

  const sections = [
    ["Unit economics", ["costPerOutcome", "successRate", "successAdjustedCost", "costPerAttempt", "fullyLoaded", "costSavings", "savingsPercent", "roi", "benefitCost", "valuePerDollar", "costToValue", "payback", "breakEven", "marginalCost"]],
    ["Productivity and human leverage", ["throughput", "productivityGain", "timeSaved", "hoursSaved", "automationRate", "escalationRate", "humanMinutes", "humanLeverage", "qualityAdjustedCost", "acceptanceRate"]],
    ["Reliability and agent consumption", ["failureRate", "retryRate", "expectedFailureCost", "riskAdjustedCost", "tokensPerOutcome", "modelCostPerOutcome", "toolCallsPerOutcome", "agentStepsPerOutcome", "retriesPerOutcome", "timeToOutcome"]],
    ["Commercial and model quality", ["grossMargin", "aiCostRevenue", "contributionMargin", "efficiency", "precision", "recall"]],
  ];

  function updateValue(key, nextValue) {
    setValues((current) => ({ ...current, [key]: nextValue === "" ? "" : Number(nextValue) }));
  }

  return (
    <main className="ai-economics-page">
      <header className="ai-economics-header">
        <div>
          <div className="ai-economics-kicker">Joz AI Economics</div>
          <h1>Agent outcome calculators</h1>
          <p>
            Translate agent performance into cost, value, productivity, risk, and quality measures. Every result updates as you change the assumptions.
          </p>
        </div>
        <div className="ai-economics-header-actions">
          <label className="ai-economics-currency">
            <span>Currency</span>
            <select value={currency} onChange={(event) => setCurrency(event.target.value)}>
              <option value="USD">USD $</option>
              <option value="EUR">EUR €</option>
              <option value="GBP">GBP £</option>
              <option value="CHF">CHF</option>
              <option value="SGD">SGD $</option>
            </select>
          </label>
          <span className="ai-economics-pill">{currency} · {Object.keys(values).length} inputs</span>
          <button type="button" className="ai-economics-reset" onClick={() => setValues(EMPTY_VALUES)}>
            Clear inputs
          </button>
        </div>
      </header>

      <section className="ai-economics-best">
        <div>
          <div className="ai-economics-kicker">Most important metric</div>
          <h2>Cost per Successful, Quality-Compliant Outcome</h2>
          <p className="ai-economics-best-formula">
            <strong>C₍FL₎ ÷ O₍Q₎</strong>
            <span>Total Fully Loaded Cost / Successful Outcomes Meeting Required Quality Threshold</span>
          </p>
        </div>
        <strong>{formatCurrency(metrics.best[2], currency)}</strong>
      </section>

      <section className="ai-economics-priority">
        <div className="ai-economics-section-heading">
          <div className="ai-economics-kicker">Start here</div>
          <h2>Most important decision metrics</h2>
          <p>These measures give the fastest view of whether an agent is economically viable, reliable, and worth scaling.</p>
        </div>
        <div className="ai-economics-priority-grid">
          {PRIORITY_METRIC_KEYS.map((key) => {
            const [label, formula, value, kind] = metrics[key];
            return <MetricCard key={key} label={label} formula={formula} mathFormula={MATH_FORMULAS[key]} value={value} kind={kind} currency={currency} featured />;
          })}
        </div>
      </section>

      <section className="ai-economics-inputs">
        <div className="ai-economics-example-note">
          <strong>Calculate on this device</strong>
          <span>Enter verified values to explore costs and outcomes. These calculations stay in your browser; no visitor telemetry is loaded.</span>
        </div>
        {INPUT_SECTIONS.map((section) => (
          <div className="ai-economics-input-section" key={section.title}>
            <div className="ai-economics-section-heading">
              <h2>{section.title}</h2>
              <p>{section.description}</p>
            </div>
            <div className="ai-economics-input-grid">
              {section.fields.map(([key, label]) => (
                <label key={key} className="ai-economics-input">
                  <span>{label}</span>
                  <input
                    type="number"
                    step="any"
                    value={values[key]}
                    onChange={(event) => updateValue(key, event.target.value)}
                  />
                </label>
              ))}
            </div>
          </div>
        ))}
      </section>

      <section className="ai-economics-results">
        <div className="ai-economics-section-heading">
          <div className="ai-economics-kicker">Calculated outcomes</div>
          <h2>Decision-ready measures</h2>
          <p>— means a required denominator is zero or the result is not defined.</p>
        </div>
        {sections.map(([title, keys]) => (
          <section className="ai-economics-result-group" key={title}>
            <h3>{title}</h3>
            <div className="ai-economics-result-grid">
              {keys.map((key) => {
                const [label, formula, value, kind] = metrics[key];
                return <MetricCard key={key} label={label} formula={formula} mathFormula={MATH_FORMULAS[key]} value={value} kind={kind} currency={currency} />;
              })}
            </div>
          </section>
        ))}
      </section>
    </main>
  );
}
